import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import adminApiClient from "api/admin";
import { PaymentsRange } from "views/Dashboard/components/PaymentsRangeFilter";
import { isNotFoundError } from "../utils/errors";

export const RETAILERS_QUERY_KEY = ["retailers"];

/** The retailers for the dropdown (from Shopify, cached by the API for 24 hours). */
export const useRetailerList = () =>
  useQuery({
    queryKey: [...RETAILERS_QUERY_KEY, "list"],
    queryFn: () => adminApiClient.getRetailers(),
  });

/** One row per retailer for the period; the period is an all-time range when empty. */
export const useRetailerStats = (range: PaymentsRange) =>
  useQuery({
    queryKey: [...RETAILERS_QUERY_KEY, "stats", range],
    queryFn: () => adminApiClient.getRetailerStats(range),
  });

export const useRetailerDetail = (
  id: string | undefined,
  range: PaymentsRange,
) =>
  useQuery({
    queryKey: [...RETAILERS_QUERY_KEY, "detail", id, range],
    queryFn: () => adminApiClient.getRetailerDetail(id as string, range),
    enabled: !!id,
    // A retailer that is gone will not come back by asking again.
    retry: (failureCount, error) => !isNotFoundError(error) && failureCount < 2,
  });

/**
 * Asks the API to reload the companies from Shopify and the sold status from
 * BigQuery now (it keeps both for 24 hours), then reloads everything on the
 * page. The stats call does both: it is not limited to a period when it comes
 * to the sold status, which is read for every retailer clock.
 */
export const useRefreshRetailers = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => adminApiClient.getRetailerStats({ refresh: true }),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: RETAILERS_QUERY_KEY }),
  });
};
